import {
  applicationStore,
  gameConfigStore,
  gameResultStore,
  popupStore,
  roundInfoStore,
  userCountStore,
  userInfoStore,
  userWinCountStore,
} from '@pascal/pascal-core/shared/core/store';
import {
  BetStatus,
  DefaultQueryParams,
  freeBetStore,
  isMobile,
  loadingStore,
  notificationStore,
  parseNumberByPrecision,
  parseQuery,
  RoundStatus,
  tableBetHistoryStore,
} from '@pascal/pascal-core';

import { PopUps } from '@pascal/pascal-core/table-line/t_types/enums/PopupTypes';
import { roundTimeStore } from '../store/roundTimeStore';

import { worker } from '../index';
import { liveConnection } from '../core';
import { gameLiveConnection } from './GameConnection';
import { doBetStore } from '../store/doBetStore';
import { gameStore } from '../store/gameStore';
import { cardsStore } from '../store/cardsStore';
import {
  BET_ACTION_TYPE_NAMES,
  BET_ACTION_TYPES,
  BET_STATUS,
  DECISION_ACTION_TYPES_NAMES_INITIAL,
  MARKET_TYPES,
} from '../enums/betTypes';
import {
  DECISION_ACCEPTED_CODE,
  DECISION_ACTION_NAMES_FOR_STORE,
  DECISION_ACTION_TYPES_FOR_STORE,
  DECISION_RESULT_EVENTS,
  SOCKET_INVOKE_TARGETS,
} from '../enums/decisionTypes';
import { mathConfig, mathJS } from '../enums/mathFormat';
import { roundActiveHandStore } from '../store/roundActiveHandStore';
import { sideBetsInfoStore } from '../store/sideBetsInfoStore';
import { winningAmountStore } from '../store/winningAmountStore';
import { MobileShuffleAnimation, WebShuffleAnimation } from '../core/constants/events';
import { CARD_HAND_TYPES, CARDS } from '../enums/cardTypes';
import { CARD_OWNER_TYPE } from '../enums/cardStatus';
import { soundService, soundTypes } from './SoundService';
import { liveBetsDataStore } from '../store/liveBetsDataStore';
import { userSettingsStore } from '@pascal/pascal-core/shared/core/store/userSettingsStore';

let timeOutId;

let animationsTimeOutId = [];
let shuffleAnimationTimeOutId;
let endShuffleAnimationTimeOutId;

class GameService {
  combineDecisionEvents = {
    [DECISION_RESULT_EVENTS.HitResult]: this.handleHitResult.bind(this),
    [DECISION_RESULT_EVENTS.StandResult]: this.handleStandResult.bind(this),
    [DECISION_RESULT_EVENTS.SplitResult]: this.handleSplitResult.bind(this),
    [DECISION_RESULT_EVENTS.DoubleResult]: this.handleDoubleResult.bind(this),
    [DECISION_RESULT_EVENTS.SurrenderResult]: this.handleSurrenderResult.bind(this),
    [DECISION_RESULT_EVENTS.InsuranceBetResult]: this.handleInsuranceBetResult.bind(this),
  };

  onGetInitialStateResult({
    rate,
    limits,
    balance,
    precision,
    roundInfo,
    userActions,
    currentBets,
    dealerPoint,
    cardResults,
    dealerCards,
    currencyCode,
    isTestPlatform,
    cuttingCardPlace,
    possibleBetActions,
    partnerHasSurrenderAction,
    userFreebets,
    gameTypes,
  }) {
    tableBetHistoryStore.setFetchPaths({ ThirdStep: '/betDetails' });
    this.handleResetDataInitial();
    popupStore.setReloadFrameForUnderMaintenanceOnRoundStart(true);
    const {
      [DefaultQueryParams.CurrencySymbol]: currencySymbol,
      [DefaultQueryParams.UserId]: userId,
    } = parseQuery(window.location.search);
    userInfoStore.setUserInfo({
      rate,
      userId,
      balance,
      precision,
      currencyCode,
      currencySymbol,
      demoBalance: 10000,
    });
    if (!userId) {
      applicationStore.setIsGuest(true);
    }
    gameStore.setPartnerHasSurrenderAction(partnerHasSurrenderAction);
    gameStore.setGameTypes(gameTypes);
    applicationStore.setIsDemo(isTestPlatform);
    applicationStore.setIsTestPlatform(isTestPlatform);
    gameStore.setDecisionDuration(roundInfo.duration);
    gameConfigStore.setLimits(limits);
    doBetStore.setQuickBets(limits.quickBets);
    if (!freeBetStore.activeFreeBet.id) {
      doBetStore.setSelectedChip(limits.quickBets[0]);
    }
    cardsStore.setCuttingCardPlace(cuttingCardPlace);
    const alreadyOpenedCards = cardResults
      ?.filter(
        ({ card, roundId }) =>
          card !== cardResults.CUTTING_CARD &&
          roundId !== roundInfo?.roundId &&
          roundId !== 0 &&
          roundId
      )
      .reverse();
    const nextCards = cardResults
      ?.filter(({ roundId }) => !roundId || (roundInfo && roundId === roundInfo?.roundId))
      .reverse();
    gameResultStore.setGameResults(nextCards);
    gameResultStore.setAdditionalResults(alreadyOpenedCards);
    userCountStore.setUsersCount(currentBets.length || 0);

    if (roundInfo.roundId) {
      roundInfoStore.setRoundId(roundInfo.roundId);
    }
    if (roundInfo && roundInfo.second && !roundInfo.decisionInfo?.decisionSecond) {
      const time = Math.floor(Math.abs(roundInfo.duration - roundInfo.second));
      roundTimeStore.setTime(time);
      this.sendWorker(time);
    }

    if (roundInfo && roundInfo.decisionInfo?.decisionSecond) {
      const decisionSecond = roundInfo.decisionInfo.decisionSecond || 1;
      const time = Math.floor(Math.abs(roundInfo.duration - decisionSecond));
      roundTimeStore.setTime(time);
      this.sendWorker(time);
    }

    if (roundInfo.second > 0 && roundInfo.duration < roundInfo.second) {
      gameStore.setIsBetsClosed(true);
      if (cuttingCardPlace < 0) {
        cardsStore.setIsCuttingCardOpen(true);
      }
    }
    if (roundInfo.shuffledSeconds) {
      this.handleShuffleAnimationInitial(roundInfo.roundId, roundInfo.shuffledSeconds);
    }
    if (!roundInfo.shuffledSeconds) {
      // const activeBetId = possibleBetActions?.betId;
      if (roundInfo.status !== RoundStatus.OnFinish) {
        cardsStore.setDealerCardsInfo({
          cards: dealerCards,
          point: dealerPoint,
        });
        if (roundInfo.userCards?.length) {
          cardsStore.setPlayerCards(
            roundInfo.userCards?.filter((userCard) => userCard.card !== CARDS.CUTTING_CARD)
          );
        }
      } else {
        tableBetHistoryStore.handleBetHistoryUpdate();
        this.handleResetRoundData(true);
        this.handleFilterResultsOpenedCards();
      }

      if (roundInfo.status === RoundStatus.OnHold) {
        roundTimeStore.resetTimerStore();
        gameStore.setDecisionDuration(null);
        console.log('Reset Timers for OnHold');
      }

      if (currentBets?.length) {
        if (currentBets[0].bet.freeBetId) {
          freeBetStore.setActivateFreeBet(
            userFreebets?.find((item) => item.id === currentBets[0].bet.freeBetId)
          );
        }
        // if (activeBetId !== currentBets[1]?.bet?.betId || !activeBetId) {
        //   gameStore.setBetId(currentBets[0].bet?.betId);
        // }
        gameStore.setBetId(currentBets[0]?.bet?.betId);
        if (possibleBetActions?.possibleBetActions.length) {
          gameStore.setPossibleBetActions(possibleBetActions?.possibleBetActions);
          roundActiveHandStore.setHandInfo({
            type: DECISION_ACTION_NAMES_FOR_STORE.isHandActive,
            bool: true,
          });
          gameStore.setHasInsurance(roundInfo?.decisionInfo?.hasInsurance);
          gameStore.setIsShowDecision(true);
        }
        if (currentBets[1]?.bet.type === BET_ACTION_TYPES.Split) {
          gameStore.setSplitBetId(currentBets[1]?.bet?.betId);
        }
        const allUserCards = currentBets.reduce((acc, cur) => {
          if (cur?.cards) {
            acc = [
              ...acc,
              ...cur.cards.map((item) => ({
                ...item,
                showCard: true,
                handType:
                  cur.bet.betId === gameStore.splitBetId
                    ? CARD_HAND_TYPES.secondHand
                    : CARD_HAND_TYPES.firstHand,
              })),
            ];
          }
          return acc;
        }, []);

        cardsStore.setPlayerCards(
          allUserCards?.filter((userCard) => userCard.card !== CARDS.CUTTING_CARD)
        );

        currentBets.forEach((item) => {
          cardsStore.setCardsTotalPointByHand({
            point: item?.pointInfo?.totalPoint,
            secondPoint: item?.pointInfo?.secondPoint,
            handType:
              item.bet.betId === gameStore.splitBetId
                ? CARD_HAND_TYPES.secondHand
                : CARD_HAND_TYPES.firstHand,
          });
        });
        const userSideBets = currentBets[0].bet.sideBets;
        const userMainBet = currentBets.reduce(
          (acc, cur) => {
            if (roundInfo.status === RoundStatus.Finished) {
              acc.winAmount = 0;
              acc.winAmount += cur.winAmount;
            }
            acc.amount += cur.bet.amount;
            return acc;
          },
          {
            amount: 0,
            type: MARKET_TYPES.main,
          }
        );

        if (
          roundInfo.status === RoundStatus.Finished ||
          roundInfo.status === RoundStatus.OnFinish
        ) {
          if (currentBets[0]?.bet.winAmount > 0) {
            roundActiveHandStore.setHandInfo({
              changedProps: {
                [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isWin]]: true,
              },
            });
          }
          if (
            currentBets[1]?.bet.winAmount > 0 &&
            currentBets[1]?.bet.type === BET_ACTION_TYPES.Split
          ) {
            roundActiveHandStore.setHandInfo({
              changedProps: {
                [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isWin]]: true,
              },
              isSecondHand: true,
            });
          }
          const totalSideBetsWin = currentBets[0]?.bet.sideBets.reduce((acc, cur) => {
            acc += cur.winAmount;
            return acc;
          }, 0);
          const totalBetWinAmount = totalSideBetsWin + userMainBet.winAmount;
          winningAmountStore.setWinningAmount(totalBetWinAmount);
          winningAmountStore.setLastWin(totalBetWinAmount);
          setTimeout(() => {
            if (!applicationStore.isDemo) {
              tableBetHistoryStore.handleBetHistoryUpdate();
              this.handleResetRoundData(true);
              this.handleFilterResultsOpenedCards();
              setTimeout(() => {
                doBetStore.resetBetsModels();
                gameStore.setToggleTimer(true);
              }, 1500);
            }
          }, 2000);
        }

        doBetStore.setBetsModel({
          betModel: [...userSideBets, userMainBet],
        });
        if (userActions?.length) {
          if (userActions[0]?.length) {
            const userFirstHandLastAction = userActions[0][userActions[0].length - 1].action;
            roundActiveHandStore.setHandInfo({
              changedProps: {
                [DECISION_ACTION_TYPES_FOR_STORE[
                  DECISION_ACTION_NAMES_FOR_STORE[
                    DECISION_ACTION_TYPES_NAMES_INITIAL[userFirstHandLastAction]
                  ]
                ]]: true,
              },
            });
          }
          if (userActions?.[1]?.length) {
            const userSecondHandLastAction = userActions[1][userActions[1].length - 1].action;
            roundActiveHandStore.setHandInfo({
              changedProps: {
                [DECISION_ACTION_TYPES_FOR_STORE[
                  DECISION_ACTION_NAMES_FOR_STORE[
                    DECISION_ACTION_TYPES_NAMES_INITIAL[userSecondHandLastAction]
                  ]
                ]]: true,
              },
              isSecondHand: true,
            });
          }
        }
        const isFirstHandBust = roundInfo.bustedBetsIds.some(
          (item) => item === currentBets[0]?.bet?.betId
        );
        const isSecondHandBust = roundInfo.bustedBetsIds.some(
          (item) => item === currentBets[1]?.bet?.betId
        );
        if (isFirstHandBust) {
          roundActiveHandStore.setHandInfo({
            changedProps: {
              [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isBust]]: true,
            },
          });
        }
        if (isSecondHandBust) {
          roundActiveHandStore.setHandInfo({
            changedProps: {
              [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isBust]]: true,
            },
            isSecondHand: true,
          });
        }
        gameStore.setHasInsurance(roundInfo.decisionInfo.hasInsurance);
        if (roundInfo.decisionInfo.hasInsurance) {
          gameStore.setIsShowDecision(true);
        }
        // if (roundInfo && roundInfo.status === RoundStatus.Finished) {
        // }
      }
      sideBetsInfoStore.setSideBetsInfo({
        side21plus3Odd: roundInfo.side21plus3Odd,
        sidePerfectPairOdd: roundInfo.sidePerfectPairOdd,
      });
    }
    loadingStore.updateLoading({ isGameInitialLoaded: true });
    popupStore.toggleReconnect(false);
    popupStore.onConnectionError(false);
    popupStore.close();
    loadingStore.updateIsAdditionalLoading(false);
  }

  handleDoBetResult({ id, amount, responseCode, userId, betData }) {
    const { isDemo } = applicationStore;
    const { balance, setDemoBalance } = userInfoStore;
    const { setBetId } = gameStore;
    if (userId === +userInfoStore.userId) {
      setBetId(id);
      if (isDemo) {
        setDemoBalance(balance - amount);
      }
      doBetStore.handleNotificationCode(responseCode);
      doBetStore.setReBet(doBetStore.lastBetModel?.betModel);
      doBetStore.setReBetAmount(amount);
    }
    userCountStore.setUsersCount(
      betData.length ? userCountStore.usersCount + 1 : userCountStore.usersCount
    );
  }

  handleDoBetResultValidation({ responseCode }) {
    doBetStore.resetBetsModels();
    doBetStore.handleNotificationCode(responseCode);
  }

  handleDoubleResultValidation({ responseCode }) {
    doBetStore.resetBetsModels();
    doBetStore.handleNotificationCode(responseCode);
  }

  handleSplitResultValidation({ responseCode }) {
    doBetStore.resetBetsModels();
    doBetStore.handleNotificationCode(responseCode);
  }

  handleRoundStart({ data: { startedRound } }) {
    gameStore.setToggleTimer(false);
    gameStore.setDecisionDuration(startedRound.duration);
    roundInfoStore.setRoundId(startedRound.roundId);
    roundTimeStore.setTime(startedRound.duration);
    this.sendWorker(startedRound.duration);
  }

  handleResultReport({ data: { resultReportData } }) {
    const {
      openedCard,
      hasBlackjack,
      cuttingCardPlace,
      sideBetsInfo,
      bustedBetsIds = [],
      betIdsAllowCard = [],
      pointInfo,
      decisions,
    } = resultReportData || {};

    const currentBetId = Number(gameStore.betId);
    const currentSplitBetId = Number(gameStore.splitBetId);

    const isFirstHandAllowed = betIdsAllowCard.includes(currentBetId);
    const isSecondHandAllowed = betIdsAllowCard.includes(currentSplitBetId);

    const isFirstHandBusted = bustedBetsIds.includes(currentBetId);
    const isSecondHandBusted = bustedBetsIds.includes(currentSplitBetId);

    if (sideBetsInfo) {
      const { calculatedSideBets = [] } = sideBetsInfo;
      if (calculatedSideBets.length) {
        const mainBet = doBetStore.lastBetModel.betModel?.find(
          (item) => item.type === MARKET_TYPES.main
        );
        const validSideBets = calculatedSideBets.filter(
          (bet) => +bet.userId === +userInfoStore.userId && bet.winAmount > 0
        );
        doBetStore.setBetsModel({
          betModel: [...validSideBets, mainBet],
        });
        sideBetsInfoStore.setSideBetsInfo(validSideBets);
      }
      sideBetsInfoStore.setSideBetsMultiplayer(sideBetsInfo);
      return;
    }

    cardsStore.setCuttingCardPlace(cuttingCardPlace);

    if (!betIdsAllowCard.length || isFirstHandAllowed || isSecondHandAllowed) {
      if (openedCard.owner !== CARD_OWNER_TYPE.dealer) {
        const isBust = !!(bustedBetsIds.length && (isFirstHandBusted || isSecondHandBusted));
        roundActiveHandStore.setHandInfo({
          changedProps: {
            [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isBust]]: isBust,
          },
          isSecondHand: isSecondHandAllowed,
        });
      } else {
        roundActiveHandStore.setHandInfo({
          changedProps: {
            [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isHandActive]]: false,
          },
        });
      }

      const betPoints = pointInfo?.betsPoint.filter((item) => {
        return item.betId === currentBetId || item.betId === currentSplitBetId;
      });

      this.handleUpdateCardResults(openedCard);

      cardsStore.updateCards({
        openedCard: {
          ...openedCard,
          handType: isSecondHandAllowed ? CARD_HAND_TYPES.secondHand : CARD_HAND_TYPES.firstHand,
        },
        hasBlackjack,
        isDouble: roundActiveHandStore.firstHandInfo.isDouble,
        secondPoint: pointInfo?.secondPoint,
        point: pointInfo?.totalPoint,
        callBack: () => {
          this.handleUpdateCardPoints({
            pointInfo,
            betPoints,
            isAllowCardSecondHand: isSecondHandAllowed,
          });
        },
      });

      gameStore.setDecisionActionsCount(decisions);
    } else {
      this.handleUpdateCardResults(openedCard);
      cardsStore.handleUpdateCardCentralBox(openedCard);
    }
  }

  handleUpdateCardResults(openedCard) {
    soundService.play(soundTypes.Card);

    const results = [...gameResultStore.gameResults];
    const cardIndex = results.findIndex((item) => item.index === openedCard.index);
    if (cardIndex > -1 && cardIndex < results.length) {
      results.splice(cardIndex, 1, openedCard);
      gameResultStore.setGameResults(results);
    }
  }

  handleUpdateCardPoints({ pointInfo, betPoints, isAllowCardSecondHand }) {
    if (pointInfo.isInitialOrDealerCards) {
      cardsStore.setCardsTotalPointByHand({
        point: pointInfo.isInitialOrDealerCards ? pointInfo?.totalPoint : betPoints?.betTotalPoint,
        secondPoint: pointInfo.isInitialOrDealerCards
          ? pointInfo.secondPoint
          : betPoints?.betSecondPoint,
        handType: isAllowCardSecondHand ? CARD_HAND_TYPES.secondHand : CARD_HAND_TYPES.firstHand,
      });
    } else {
      betPoints.forEach((item) => {
        cardsStore.setCardsTotalPointByHand({
          point: pointInfo.isInitialOrDealerCards ? pointInfo?.totalPoint : item?.betTotalPoint,
          secondPoint: pointInfo.isInitialOrDealerCards
            ? pointInfo.secondPoint
            : item?.betSecondPoint,
          handType:
            item.betId === gameStore.splitBetId
              ? CARD_HAND_TYPES.secondHand
              : CARD_HAND_TYPES.firstHand,
        });
      });
    }
  }

  handleRoundFin({ data: { bets } }) {
    const { userId } = userInfoStore;
    const { freeBetTotalWinCalculator } = freeBetStore;
    winningAmountStore.setIsFinishedRound(true);

    const currentUserBets = bets.filter((bet) => +bet.userId === +userId);
    if (currentUserBets.length) {
      const totalWinAmount = currentUserBets.reduce((acc, cur) => {
        acc = parseNumberByPrecision(1, acc + cur.winAmount, +userInfoStore.precision);
        return acc;
      }, 0);

      winningAmountStore.setWinningAmount(totalWinAmount);
      winningAmountStore.setLastWin(totalWinAmount);

      const totalMainBetInfo = currentUserBets.reduce(
        (acc, cur) => {
          const mainBet = cur.additionalParams?.markets?.find(
            (item) => item.type === MARKET_TYPES.main
          );
          const mainInfo = mainBet || cur;

          acc.amount += mainInfo.amount;
          acc.winAmount += mainInfo.winAmount;

          if (mainBet) {
            acc.odd = mainInfo.odd;
            acc.status = mainInfo.status;
          }

          return acc;
        },
        {
          amount: 0,
          winAmount: 0,
          type: MARKET_TYPES.main,
        }
      );
      const betsByNames = currentUserBets.reduce((acc, cur) => {
        acc[BET_ACTION_TYPE_NAMES[cur.additionalParams.type]] = cur;
        return acc;
      }, {});
      const sideBetsModel = currentUserBets[0].additionalParams.markets.filter(
        (item) => item.type !== MARKET_TYPES.main
      );

      doBetStore.setBetsModel({
        betModel: [totalMainBetInfo, ...sideBetsModel],
      });

      setTimeout(() => {
        gameLiveConnection.getBalance();
      }, 500);

      if (currentUserBets.some((bet) => bet.freeBetId)) {
        freeBetTotalWinCalculator(currentUserBets);
      }

      roundActiveHandStore.setHandInfo({
        changedProps: {
          [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isWin]]:
            totalMainBetInfo.status === BET_STATUS.Win,
          [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isLose]]:
            totalMainBetInfo.status === BET_STATUS.Lose,
          [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isPush]]:
            totalMainBetInfo.odd === 1,
        },
        isFirstHand: true,
      });
      if (betsByNames[BET_ACTION_TYPE_NAMES[BET_ACTION_TYPES.Split]]) {
        roundActiveHandStore.setHandInfo({
          changedProps: {
            [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isWin]]:
              betsByNames[BET_ACTION_TYPE_NAMES[BET_ACTION_TYPES.Split]].status === BET_STATUS.Win,
            [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isLose]]:
              betsByNames[BET_ACTION_TYPE_NAMES[BET_ACTION_TYPES.Split]].status !== BET_STATUS.Win,
            [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isPush]]:
              betsByNames[BET_ACTION_TYPE_NAMES[BET_ACTION_TYPES.Split]].odd === 1,
          },
          isSecondHand: true,
        });
      }
    }

    const winningUsers = bets.filter((user) => user.status === BetStatus.Win);
    const winnersList = winningUsers.reduce((acc, cur) => {
      const existingUser = acc.find((item) => +item.userId === +cur.userId);

      if (existingUser) {
        existingUser.winAmount += cur.winAmount;
      } else if (cur.winAmount > 0) {
        acc.push({ ...cur });
      }
      return acc;
    }, []);
    if (winnersList.length) {
      winnersList.sort((a, b) => b.winAmount - a.winAmount);
      userWinCountStore.setUserWiningBets(winnersList);
    }
    setTimeout(() => {
      if (!applicationStore.isDemo) {
        tableBetHistoryStore.handleBetHistoryUpdate();
        this.handleResetRoundData(true);
        this.handleFilterResultsOpenedCards();
      }
    }, 2000);
  }

  handleBetsClosed({ data: { gameTypeId, roundId } }) {
    gameStore.setIsBetsClosed(true);
    const { betsModels } = doBetStore;
    const { activeFreeBet } = freeBetStore;

    const betData = betsModels[betsModels.length - 1]?.betModel;
    roundTimeStore.setWaitingText('NO_MORE_BETS');
    setTimeout(() => {
      roundTimeStore.setWaitingText('DEALING_TIME');
      setTimeout(() => {
        roundTimeStore.setWaitingText('');
      }, 1000);
    }, 1000);
    if (betData) {
      if (userSettingsStore.isSoundOn) {
        soundService.playBetAccepted();
      }
      gameLiveConnection.doBet(
        activeFreeBet.id
          ? {
              betData,
              gameTypeId,
              roundId,
              freeBetId: activeFreeBet.id,
            }
          : {
              betData,
              gameTypeId,
              roundId,
            }
      );
    }
  }

  handleChipAdded({ isAdded }) {
    if (!isAdded) {
      doBetStore.setIsFirstBetInRound(false);
    }
  }

  handleDecisionTime({ data: { betIdsAllowDoActions, possibleActions, hasInsurance, duration } }) {
    const { activeFreeBet } = freeBetStore;
    const { lastBetModel } = doBetStore;
    const isAllowCardFirstHand = betIdsAllowDoActions?.includes(gameStore.betId);
    const isAllowCardSecondHand = betIdsAllowDoActions?.includes(gameStore.splitBetId);
    const haveAnBets = lastBetModel?.betModel?.[0];

    if (isAllowCardSecondHand) {
      roundActiveHandStore.setHandInfo({
        type: DECISION_ACTION_NAMES_FOR_STORE.isHandActive,
        bool: false,
      });
      gameStore.setBetId(null);
    }
    if (betIdsAllowDoActions && (isAllowCardFirstHand || isAllowCardSecondHand)) {
      gameStore.setPossibleBetActions(possibleActions);
    }
    roundActiveHandStore.setHandInfo({
      type: DECISION_ACTION_NAMES_FOR_STORE.isHandActive,
      bool: true,
    });

    if (haveAnBets && !activeFreeBet?.id) {
      gameStore.setHasInsurance(hasInsurance);
    }
    gameStore.setIsShowDecision(true);
    gameStore.setDecisionDuration(duration);
    roundTimeStore.setTime(duration);
    this.sendWorker(duration);
  }

  handleDecisionTimeEnded() {
    gameStore.setDecisionDuration(null);
    gameStore.setLiveDecisionActionCounts();
    gameStore.setHasInsurance(false);
    gameStore.setIsShowDecision(false);
    gameStore.setPossibleBetActions(null);
    gameStore.setIsSelectedDecision(false);
    gameStore.setToggleTimer(false);
  }

  sendDecisionAction(type) {
    const { betId, splitBetId, isSelectedDecision, setIsSelectedDecision, setToggleTimer } =
      gameStore;
    if (isSelectedDecision) return;
    const { roundId } = roundInfoStore;
    const { activeFreeBet } = freeBetStore;
    setIsSelectedDecision(true);
    setToggleTimer(true);
    const payload = {
      roundId,
      betId: betId || splitBetId,
      ...(activeFreeBet && activeFreeBet.userId === Number(userInfoStore.userId)
        ? { freeBetId: activeFreeBet.id }
        : {}),
    };
    gameLiveConnection.sendDecisionAction(SOCKET_INVOKE_TARGETS[type], payload);
  }

  handleDecisionEvents(eventName, data) {
    const { responseCode, userId } = data;
    if (+userId === +userInfoStore.userId) {
      if (!data.hasOwnProperty('responseCode') || responseCode === DECISION_ACCEPTED_CODE) {
        this.combineDecisionEvents[eventName](data);
        gameStore.setPossibleBetActions(null);
        // gameStore.setIsShowDecision(false);
      }
      gameStore.setIsSelectedDecision(false);
    }
    if (eventName) {
      gameStore.setLiveDecisionActionCounts(eventName);
    }
  }

  handleHitResult() {
    roundActiveHandStore.setHandInfo({
      type: DECISION_ACTION_NAMES_FOR_STORE.isHit,
      bool: true,
    });
  }

  handleStandResult() {
    roundActiveHandStore.setHandInfo({
      changedProps: {
        [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isHit]]: false,
        [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isStand]]: true,
      },
    });
  }

  handleAutoStandResult({ data }) {
    data.userDecisions.forEach((item) => {
      if (+item.userId === +userInfoStore.userId) {
        roundActiveHandStore.setHandInfo({
          changedProps: {
            [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isHit]]: false,
            [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isStand]]: true,
          },
        });
      }
    });
  }

  handleSplitResult({ responseCode, id, amount, betsPoint }) {
    if (responseCode === DECISION_ACCEPTED_CODE) {
      gameStore.setSplitBetId(id);
      cardsStore.setFirstSplitCard();
      const sideBets = doBetStore.lastBetModel.betModel.filter(
        (item) => item.type !== MARKET_TYPES.main
      );
      doBetStore.setBetsModel({
        betModel: [
          {
            type: MARKET_TYPES.main,
            amount: +mathJS.format(amount * 2, mathConfig),
          },
          ...sideBets,
        ],
      });
      const userPoint = betsPoint.filter(
        (item) => item.betId === gameStore.betId || item.betId === gameStore.splitBetId
      );

      userPoint.forEach((item) => {
        cardsStore.setCardsTotalPointByHand({
          point: item?.totalPoint,
          handType:
            item.betId === gameStore.splitBetId
              ? CARD_HAND_TYPES.secondHand
              : CARD_HAND_TYPES.firstHand,
        });
      });
    }
  }

  handleDoubleResult({ amount, responseCode }) {
    const { lastBetModel, setBetsModel } = doBetStore;
    if (responseCode === DECISION_ACCEPTED_CODE) {
      roundActiveHandStore.setHandInfo({
        type: DECISION_ACTION_NAMES_FOR_STORE.isDouble,
        bool: true,
      });
      const sideBets = lastBetModel.betModel.filter((item) => item.type !== MARKET_TYPES.main);
      const mainBet = lastBetModel.betModel.find((item) => item.type === MARKET_TYPES.main);
      setBetsModel({
        betModel: [
          ...sideBets,
          {
            type: mainBet.type,
            amount: +mathJS.format(amount * 2, mathConfig),
          },
        ],
      });
    }
  }

  handleSurrenderResult({ userId, winAmount }) {
    if (+userId === +userInfoStore.userId) {
      const { lastBetModel, setBetsModel } = doBetStore;
      roundActiveHandStore.setHandInfo({
        type: DECISION_ACTION_NAMES_FOR_STORE.isSurrender,
        bool: true,
      });
      const sideBets = lastBetModel.betModel.filter((item) => item.type !== MARKET_TYPES.main);
      const mainBet = lastBetModel.betModel.find((item) => item.type === MARKET_TYPES.main);

      setTimeout(() => {
        setBetsModel({
          betModel: [...sideBets, { amount: mainBet.amount / 2, type: mainBet.type }],
        });

        const totalWinAmount = parseNumberByPrecision(1, winAmount, +userInfoStore.precision);

        winningAmountStore.setWinningAmount(totalWinAmount);
        winningAmountStore.setLastWin(totalWinAmount);

        gameLiveConnection.getBalance();
      }, 500);
    }
  }

  handleInsuranceValidation({ responseCode }) {
    notificationStore.setNotificationType(responseCode);
  }

  handleInsuranceBetResult({ responseCode, amount }) {
    const { lastBetModel, setBetsModel } = doBetStore;
    doBetStore.handleNotificationCode(responseCode);

    if (responseCode === DECISION_ACCEPTED_CODE) {
      gameStore.setHasInsurance(false);
      gameStore.setToggleInsuranceAnimatedCard();

      const sideBets = lastBetModel.betModel.filter((item) => item.type !== MARKET_TYPES.main);
      const mainBet = lastBetModel.betModel.find((item) => item.type === MARKET_TYPES.main);
      setBetsModel({
        betModel: [...sideBets, { amount: mainBet.amount + amount, type: mainBet.type }],
      });
    }
  }

  handleRoundPostponed() {
    this.handleResetRoundData();
  }

  handleNewCardResult({ data: { cardInfo, gameTypeId } }) {
    const { expiredCards, newShuffledCards, cuttingCardPlace } = cardInfo;
    this.handleShuffleStarting({ expiredCards, newShuffledCards, cuttingCardPlace, gameTypeId });
  }

  socketRecursiveReconnecting() {
    try {
      liveConnection
        .restartGameConnection()
        .then((connectionData) => {
          liveConnection.connectionInitCallback(connectionData);
          clearTimeout(timeOutId);
        })
        .catch(() => {
          timeOutId = setTimeout(() => {
            this.socketRecursiveReconnecting();
          }, 4000);
        });
    } catch (err) {
      // popupStore.open({ name: PopUps.ConnectionError });
      popupStore.onConnectionError(true);
      popupStore.toggleReconnect(true);
    }
  }

  handleReconnecting(error) {
    if (error && error.message) {
      // eslint-disable-next-line
      popupStore.open({ name: PopUps.Reconnect });
      popupStore.toggleReconnect(true);
    } else {
      GameService.ReconnectCount += 1;
      popupStore.open({ name: PopUps.Reconnect });
      popupStore.toggleReconnect(true);
    }
  }

  handleReconnected() {
    GameService.ReconnectCount = 0;
    if (popupStore.popupName !== PopUps.ConnectionError) {
      gameLiveConnection.sendGameGetInitialState(false).then(() => {});
    }
  }

  handleConnectionError() {
    if (GameService.ReconnectCount) {
      // popupStore.open({ name: PopUps.ConnectionError });
      popupStore.onConnectionError(true);
    }
  }

  countDown({ data: time }) {
    roundTimeStore.setTime(time);
  }

  sendWorker(timerValue) {
    if (timerValue) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(timerValue);
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }

  handleShuffleStarting({ expiredCards, newShuffledCards, cuttingCardPlace, gameTypeId }) {
    if (Number(gameTypeId) !== Number(gameConfigStore?.gameTypeId)) {
      this.clearShuffleAnimations();
      return;
    }
    this.handleResetRoundData(true);

    const lastAndNextResults = expiredCards.reverse().reduce(
      (acc, cur) => {
        if (cur.roundId) {
          acc.alreadyOpenedCards.push(cur);
        } else {
          acc.nextCards.push(cur);
        }
        return acc;
      },
      {
        alreadyOpenedCards: [],
        nextCards: [],
      }
    );
    gameResultStore.setGameResults(lastAndNextResults.nextCards);
    gameResultStore.setAdditionalResults(lastAndNextResults.alreadyOpenedCards);
    gameStore.setIsBetsClosed(true);
    const animData = isMobile() ? MobileShuffleAnimation : WebShuffleAnimation;
    cardsStore.setIsShuffle(true);
    cardsStore.setIsCuttingCardOpen(false);
    let animationDuration = 0;
    for (let i = 0; i < animData.length; i += 1) {
      shuffleAnimationTimeOutId = setTimeout(() => {
        cardsStore.setShuffleAnimationType(animData[i].payload.current.join(' '));
      }, animationDuration);
      animationsTimeOutId.push(shuffleAnimationTimeOutId);
      animationDuration += animData[i].payload.duration;
    }

    endShuffleAnimationTimeOutId = setTimeout(() => {
      gameResultStore.setGameResults(newShuffledCards.reverse());
      gameResultStore.setAdditionalResults([]);
      cardsStore.setOpenedCardsCount(0);
      cardsStore.setCuttingCardPlace(cuttingCardPlace);
      cardsStore.setIsShuffle(false);
      gameStore.setIsBetsClosed(false);
    }, animationDuration);
    animationsTimeOutId.push(endShuffleAnimationTimeOutId);
  }

  clearShuffleAnimations() {
    animationsTimeOutId.forEach((id) => clearTimeout(id));
    animationsTimeOutId = [];
    cardsStore.setShuffleAnimationType('');
    cardsStore.setIsShuffle(false);
    this.handleResetRoundData();
    gameStore.setIsBetsClosed(false);
    gameResultStore.setAdditionalResults([]);
    cardsStore.setOpenedCardsCount(0);
  }

  handleShuffleAnimationInitial(roundId, shuffledSeconds) {
    if (Number(roundId) !== Number(gameConfigStore?.gameTypeId)) {
      this.handleResetRoundData(true);
      this.clearShuffleAnimations();
      return;
    }

    const animData = isMobile() ? MobileShuffleAnimation : WebShuffleAnimation;
    cardsStore.setIsShuffle(true);
    let animationDuration = 0;
    let skipAnimationSecond = 0;
    for (let i = 0; i < animData.length; i += 1) {
      skipAnimationSecond += animData[i].payload.duration;
      if (shuffledSeconds * 1000 + 700 < skipAnimationSecond) {
        shuffleAnimationTimeOutId = setTimeout(() => {
          cardsStore.setShuffleAnimationType(animData[i].payload.current.join(' '));
        }, animationDuration);
        animationsTimeOutId.push(shuffleAnimationTimeOutId);
        const skipTime = animData[i].payload.duration - (skipAnimationSecond - shuffledSeconds);
        animationDuration += skipTime < 0 ? animData[i].payload.duration : skipTime;
      }
    }

    if (shuffledSeconds * 1000 < skipAnimationSecond) {
      animationDuration += 1000;
      endShuffleAnimationTimeOutId = setTimeout(() => {
        gameResultStore.setAdditionalResults([]);
        cardsStore.setOpenedCardsCount(0);
      }, animationDuration);
      animationsTimeOutId.push(shuffleAnimationTimeOutId);
    }

    cardsStore.setShuffleAnimationType('');
    cardsStore.setIsShuffle(false);
  }

  handleFilterResultsOpenedCards() {
    const alreadyOpenedCards = gameResultStore.gameResults.filter(({ roundId }) => roundId);
    gameResultStore.setAdditionalResults([
      ...alreadyOpenedCards,
      ...gameResultStore.additionalResults,
    ]);
    const results = [...gameResultStore.gameResults];
    results.length -= alreadyOpenedCards.length;
    gameResultStore.setGameResults(results);
  }

  handleResetRoundData(isInRoundFin) {
    cardsStore.setIsDarkCardOpened(false);
    userCountStore.setUsersCount(0);
    roundInfoStore.setRoundId(null);
    roundTimeStore.setWaitingText('');
    cardsStore.resetCards();
    gameStore.setLiveDecisionActionCounts();
    gameStore.setDecisionActionsCount(null);
    sideBetsInfoStore.setSideBetsInfo(null);
    sideBetsInfoStore.setSideBetsMultiplayer(null);
    doBetStore.resetBetsModels();
    doBetStore.setIsFirstBetInRound(false);
    gameStore.setHasInsurance(false);
    gameStore.setToggleTimer(false);
    if (isInRoundFin && !cardsStore.isCuttingCardOpen) {
      gameStore.setIsBetsClosed(false);
    }
    if (!isInRoundFin) {
      gameStore.setIsBetsClosed(false);
    }
    gameStore.setPossibleBetActions(null);
    gameStore.setIsSelectedDecision(false);
    winningAmountStore.clearWinInfo();
    roundActiveHandStore.resetActiveHandInfo();
  }

  handleResetDataInitial() {
    cardsStore.resetCards();
    doBetStore.resetDoBetStore();
    roundTimeStore.resetTimerStore();
    this.clearShuffleAnimations();
    this.handleResetRoundData();
    gameStore.resetGameStore();
    liveBetsDataStore.resetLiveBetsData([]);
    userWinCountStore.setUserWiningBets(null);
    sideBetsInfoStore.setSideBetsInfo(null);
    sideBetsInfoStore.setSideBetsMultiplayer(null);
    roundActiveHandStore.resetActiveHandInfo();
    winningAmountStore.clearWinInfo();
    worker.removeEventListener('message', this.countDown);
  }
}

export const gameService = new GameService();
